// Soccer pitch drawer. Moved verbatim from the old libs/pitch/src/2d/
// draw-pitch.ts body — the only change is the name (`drawSoccerPitch`) and
// the file location. It still uses the P1..P34 key mapping the backend
// emits for soccer pitches.
//
// Pitch point mapping:
// P1/P3 = bottom-left/right corners (y=0 side)
// P10/P8 = top-left/right corners (y=max side)
// P2/P9 = halfway line ends (bottom/top)
// P25 = center spot
//
// Left penalty area:  P14/P11 outer corners, P20/P23 box corners
// Left goal area:     P13/P12 outer, P17/P18 inner
// Left goal:          P15/P16
// Left penalty arc center: P19
//
// Right penalty area: P4/P7, P29/P26
// Right goal area:    P5/P6, P32/P31
// Right goal:         P34/P33
// Right penalty arc center: P30
//
// Center circle center: P25, radius derived from P24 (on circle edge).
import { PITCH_FILL, PITCH_LINE, PITCH_LINE_WIDTH } from '../../constants'
import type { PitchCoordinates, WorldToCanvas } from '../../types'
import type { PitchDrawOptions } from '../types'

export function drawSoccerPitch(
  ctx: CanvasRenderingContext2D,
  pitch: PitchCoordinates,
  w2c: WorldToCanvas,
  canvasWidth: number,
  canvasHeight: number,
  options?: PitchDrawOptions
) {
  const fillColor = options?.fill ?? PITCH_FILL
  const lineColor = options?.line ?? PITCH_LINE
  const lineW = options?.lineWidth ?? PITCH_LINE_WIDTH

  const p = (key: string) => {
    const pt = pitch[key]
    if (!pt) return { cx: 0, cy: 0 }
    return w2c(pt.x, pt.y)
  }

  ctx.fillStyle = fillColor
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  ctx.strokeStyle = lineColor
  ctx.lineWidth = lineW
  ctx.lineJoin = 'miter'

  const p1 = p('P1')
  const p3 = p('P3')
  const p8 = p('P8')
  const p10 = p('P10')
  ctx.beginPath()
  ctx.moveTo(p1.cx, p1.cy)
  ctx.lineTo(p3.cx, p3.cy)
  ctx.lineTo(p8.cx, p8.cy)
  ctx.lineTo(p10.cx, p10.cy)
  ctx.closePath()
  ctx.stroke()

  const p2 = p('P2')
  const p9 = p('P9')
  ctx.beginPath()
  ctx.moveTo(p2.cx, p2.cy)
  ctx.lineTo(p9.cx, p9.cy)
  ctx.stroke()

  const p25 = p('P25')
  const p24 = p('P24')
  const centerRadius = Math.sqrt(
    (p25.cx - p24.cx) ** 2 + (p25.cy - p24.cy) ** 2
  )
  ctx.beginPath()
  ctx.arc(p25.cx, p25.cy, centerRadius, 0, Math.PI * 2)
  ctx.stroke()

  ctx.beginPath()
  ctx.arc(p25.cx, p25.cy, 3, 0, Math.PI * 2)
  ctx.fillStyle = lineColor
  ctx.fill()

  drawRect(ctx, p('P14'), p('P20'), p('P23'), p('P11'))
  drawRect(ctx, p('P13'), p('P17'), p('P18'), p('P12'))

  const p15 = p('P15')
  const p16 = p('P16')
  const goalDepth = 6
  const leftGoalDir = p1.cx < p3.cx ? -1 : 1
  ctx.beginPath()
  ctx.moveTo(p15.cx, p15.cy)
  ctx.lineTo(p15.cx + goalDepth * leftGoalDir, p15.cy)
  ctx.lineTo(p16.cx + goalDepth * leftGoalDir, p16.cy)
  ctx.lineTo(p16.cx, p16.cy)
  ctx.stroke()

  const p19 = p('P19')
  drawPenaltyArc(
    ctx,
    p19,
    p('P20'),
    p('P23'),
    centerRadius,
    'left',
    p1,
    p3,
    lineColor,
    lineW
  )

  drawRect(ctx, p('P4'), p('P29'), p('P26'), p('P7'))
  drawRect(ctx, p('P5'), p('P32'), p('P31'), p('P6'))

  const p34 = p('P34')
  const p33 = p('P33')
  const rightGoalDir = p3.cx > p1.cx ? 1 : -1
  ctx.beginPath()
  ctx.moveTo(p34.cx, p34.cy)
  ctx.lineTo(p34.cx + goalDepth * rightGoalDir, p34.cy)
  ctx.lineTo(p33.cx + goalDepth * rightGoalDir, p33.cy)
  ctx.lineTo(p33.cx, p33.cy)
  ctx.stroke()

  const p30 = p('P30')
  drawPenaltyArc(
    ctx,
    p30,
    p('P29'),
    p('P26'),
    centerRadius,
    'right',
    p1,
    p3,
    lineColor,
    lineW
  )

  ctx.fillStyle = lineColor
  ctx.beginPath()
  ctx.arc(p19.cx, p19.cy, 3, 0, Math.PI * 2)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(p30.cx, p30.cy, 3, 0, Math.PI * 2)
  ctx.fill()
}

function drawRect(
  ctx: CanvasRenderingContext2D,
  a: { cx: number; cy: number },
  b: { cx: number; cy: number },
  c: { cx: number; cy: number },
  d: { cx: number; cy: number }
) {
  ctx.beginPath()
  ctx.moveTo(a.cx, a.cy)
  ctx.lineTo(b.cx, b.cy)
  ctx.lineTo(c.cx, c.cy)
  ctx.lineTo(d.cx, d.cy)
  ctx.closePath()
  ctx.stroke()
}

function drawPenaltyArc(
  ctx: CanvasRenderingContext2D,
  center: { cx: number; cy: number },
  boxCorner1: { cx: number; cy: number },
  boxCorner2: { cx: number; cy: number },
  radius: number,
  side: 'left' | 'right',
  p1: { cx: number; cy: number },
  p3: { cx: number; cy: number },
  lineColor: string,
  lineW: number
) {
  const boxX =
    side === 'left'
      ? Math.max(boxCorner1.cx, boxCorner2.cx)
      : Math.min(boxCorner1.cx, boxCorner2.cx)

  ctx.save()
  ctx.beginPath()
  if (side === 'left') {
    ctx.rect(
      boxX,
      Math.min(boxCorner1.cy, boxCorner2.cy) - 1,
      center.cx - boxX + radius + 10,
      Math.abs(boxCorner2.cy - boxCorner1.cy) + 2
    )
  } else {
    ctx.rect(
      center.cx - radius - 10,
      Math.min(boxCorner1.cy, boxCorner2.cy) - 1,
      boxX - center.cx + radius + 10,
      Math.abs(boxCorner2.cy - boxCorner1.cy) + 2
    )
  }
  ctx.clip()

  ctx.beginPath()
  ctx.arc(center.cx, center.cy, radius, 0, Math.PI * 2)
  ctx.strokeStyle = lineColor
  ctx.lineWidth = lineW
  ctx.stroke()
  ctx.restore()
}
